import { useMemo } from 'react';
import type { DrillDownRow } from '../types';
import { formatDate, formatCurrency } from '../utils';
import { FinancialEmptyState } from '../../../shared/components';

interface TransactionsTableProps {
  rows: DrillDownRow[];
  maxRows?: number;
  /** Optional callback to clear filters when no transactions are found */
  onClearFilters?: () => void;
}

export function TransactionsTable({ rows, maxRows = 100, onClearFilters }: TransactionsTableProps): JSX.Element {
  // Determine visible columns - must be before early return
  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    const priority = ['Data', 'Fornecedor', 'Cliente', 'Natureza', 'Documento', 'CategoriaGerencial', 'Job', 'CentroResultado', 'Valor'];
    const available = new Set(Object.keys(rows[0] || {}));
    return priority.filter(col => available.has(col));
  }, [rows]);

  if (rows.length === 0) {
    return (
      <FinancialEmptyState
        variant="filter"
        title="Nenhuma transação encontrada"
        description="Não há transações para os filtros selecionados"
        onAction={onClearFilters}
      />
    );
  }

  const columnLabels: Record<string, string> = {
    Data: 'Data',
    Documento: 'Documento',
    Fornecedor: 'Fornecedor',
    Cliente: 'Cliente',
    Natureza: 'Natureza',
    CategoriaGerencial: 'Categoria',
    Job: 'Job',
    CentroResultado: 'Centro',
    Valor: 'Valor',
  };

  return (
    <div className="overflow-x-auto">
      <table className="table-financial w-full border-collapse text-sm">
        <thead>
          <tr className="bg-brand-dark-light">
            {columns.map(col => (
              <th
                key={col}
                className={`
                  py-3 px-2 border-b-2 border-brand-primary whitespace-nowrap text-white font-display
                  ${col === 'Valor' ? 'text-right' : 'text-left'}
                `}
              >
                {columnLabels[col] || col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, maxRows).map((row, index) => (
            <tr
              key={index}
              className={`table-row-financial ${index % 2 === 0 ? 'bg-brand-dark' : 'bg-brand-dark-light'}`}
            >
              {columns.map(col => {
                const value = row[col];
                const isNegative = col === 'Valor' && (Number(value) || 0) < 0;

                return (
                  <td
                    key={col}
                    className={`
                      py-2 px-2 border-b border-brand-dark-lighter max-w-[200px] overflow-hidden text-ellipsis font-body
                      ${col === 'Valor' ? 'text-right font-semibold' : 'text-left'}
                      ${isNegative ? 'text-error' : 'text-white'}
                      ${col === 'Data' ? 'whitespace-nowrap' : ''}
                    `}
                  >
                    {col === 'Data' ? formatDate(value as string) :
                     col === 'Valor' ? formatCurrency(value as number) :
                     value ?? '-'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <div className="p-4 text-center text-brand-gray text-sm font-body">
          Mostrando {maxRows} de {rows.length} transacoes
        </div>
      )}
    </div>
  );
}
